import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import api from "./api";
import {handleResponseStatus, updateIdField, updateItem} from "./utils";

const initialState = {
    items: [],
    createStatus: "idle", // 'idle' | 'loading' | 'success' | 'failed'
    fetchStatus: "idle", // 'idle' | 'loading' | 'success' | 'failed'
    fetchByIdStatus: "idle", // 'idle' | 'loading' | 'success' | 'failed'
    fetchVendorStatus: "idle", // 'idle' | 'loading' | 'success' | 'failed'
    updateStatus: "idle", // 'idle' | 'loading' | 'success' | 'failed'
    deleteStatus: "idle", // 'idle' | 'loading' | 'success' | 'failed'
    pagination: {},
    error: null
};

export const createInternalOrder = createAsyncThunk('purchase/createInternalOrder', async ({roleSign, data}) => {
    try {
        const response = await api.post(`${roleSign}/internalBranch`, data);
        return {...response};
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

export const fetchInternalOrder = createAsyncThunk('purchase/fetchInternalOrder', async ({
                                                                                             scopeId,
                                                                                             roleSign,
                                                                                             page,
                                                                                             limit,
                                                                                             branchType
                                                                                         } = {}) => {
    try {
        let response;
        const params = new URLSearchParams();
        if (page) params.append("page", page);
        if (limit) params.append("limit", limit);
         if (branchType) params.append("branchType", branchType);

        const queryString = params.toString();
        const fullParams = queryString ? `&${queryString}` : '';

        response = await api.get(`/${roleSign}/internalBranch?id=${scopeId}${fullParams}`);
        return {...response};
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

export const fetchInternalOrderById = createAsyncThunk('purchase/fetchInternalOrderById', async ({
                                                                                                     roleSign,
                                                                                                     scopeId,
                                                                                                     id
                                                                                                 }) => {
    try {
        let response;
        response = await api.get(`/${roleSign}/internalBranch/${id}`);
        return {...response};
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

export const updateInternalOrder = createAsyncThunk('purchase/updateInternalOrder', async ({
                                                                                               scopeId,
                                                                                               roleSign,
                                                                                               data
                                                                                           }) => {
    try {
        let response;
        response = await api.patch(`/${roleSign}/internalBranch/${data._id}?id=${scopeId}`, data);
        return {...response};
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

const internalOrderSlice = createSlice({
    name: "internalOrder",
    initialState,
    reducers: {
        resetCreateStatus(state) {
            state.createStatus = 'idle';
        },
        resetUpdateStatus(state) {
            state.updateStatus = 'idle';
        },
        resetDeleteStatus(state) {
            state.deleteStatus = 'idle';
        },
        resetEditStatus(state) {
            state.editStatus = 'idle';
        }
    },
    extraReducers: (builder) => {
        builder

            //create internal order
            .addCase(createInternalOrder.pending, (state) => {
                state.createStatus = 'loading';
            })
            .addCase(createInternalOrder.fulfilled, (state, action) => {
                const {createStatus, error} = handleResponseStatus(action.payload, 'createStatus');
                state.createStatus = createStatus;
                state.error = error;
            })
            .addCase(createInternalOrder.rejected, (state, action) => {
                state.createStatus = 'failed';
                state.error = action.error.message;
            })

            // Fetch Purchase
            .addCase(fetchInternalOrder.pending, (state) => {
                state.fetchStatus = 'loading';
            })
            .addCase(fetchInternalOrder.fulfilled, (state, action) => {
                const {fetchStatus, error} = handleResponseStatus(action.payload, 'fetchStatus');
                const {data, ...pagination} = action.payload.data;
                state.items = fetchStatus === "success" ? updateIdField(data) : state.items;
                state.fetchStatus = fetchStatus;
                state.pagination = pagination;
                state.error = error;
            })
            .addCase(fetchInternalOrder.rejected, (state, action) => {
                state.fetchStatus = 'failed';
                state.error = action.error.message;
            })

            // Fetch Internal order by ID
            .addCase(fetchInternalOrderById.pending, (state) => {
                state.fetchByIdStatus = 'loading';
            })
            .addCase(fetchInternalOrderById.fulfilled, (state, action) => {
                const {fetchByIdStatus, error} = handleResponseStatus(action.payload, 'fetchByIdStatus');
                state.items = fetchByIdStatus === "success" ? updateItem(action.payload.data.data, state.items) : state.items;
                state.fetchByIdStatus = fetchByIdStatus;
                state.error = error;
            })
            .addCase(fetchInternalOrderById.rejected, (state, action) => {
                state.fetchByIdStatus = 'failed';
                state.error = action.error.message;
            })

            // Update Purchase
            .addCase(updateInternalOrder.pending, (state) => {
                state.updateStatus = 'loading';
            })
            .addCase(updateInternalOrder.fulfilled, (state, action) => {
                const {updateStatus, error} = handleResponseStatus(action.payload, 'updateStatus');
                state.updateStatus = updateStatus;
                state.error = error;
            })
            .addCase(updateInternalOrder.rejected, (state, action) => {
                state.updateStatus = 'failed';
                state.error = action.error.message;
            })
    }
});

export const {resetCreateStatus, resetUpdateStatus, resetDeleteStatus, resetEditStatus} = internalOrderSlice.actions;
export default internalOrderSlice.reducer;
